// "… is typing" for chats (mobile: src/utils/chatTyping.js + home/v2/messages/pro/proChatLive.js).
//
// Sending: POST /group/:id/checkIsTyping { isTyping: true | false } (GroupController.
// checkIsTyping). The backend then blasts an event named after the group id:
//   { fullName, typingData: [{ user, group, isTyping, isOneToOne, typingUserFullName }] }
// typingData lists the members who should see it (the typist has no entry of
// their own). The indicator runs from the first keystroke for as long as the
// typist's field keeps focus and holds text: the sender repeats `true` every
// TYPING_HEARTBEAT_MS meanwhile and sends `false` on blur, send, an emptied field
// or leaving the chat. Receivers hide it on `false`, or TYPING_CLEAR_MS after
// the last event when the `false` never came (closed tab, lost connection).

import { callAPI } from "../helper/apiUtils";

export const TYPING_CLEAR_MS = 6000;
const TYPING_HEARTBEAT_MS = 2000;
// A blur followed by a focus within this time (a mention picked with the mouse) keeps it running.
const TYPING_BLUR_GRACE_MS = 400;

const post = (groupId, isTyping) =>
  callAPI(`/group/${groupId}/checkIsTyping`, {}, "POST", { id: groupId, isTyping }).catch(() => {});

/**
 * Typing state of one composer (failures are ignored):
 *   typed(groupId, text)  on every input; an empty text stops it
 *   focus() / blur()      of the field
 *   stop()                sent, field emptied, chat left
 */
export function createTypingSender() {
  let gid = null;
  let beat = null;
  let blurTimer = null;
  const stop = () => {
    clearTimeout(blurTimer);
    blurTimer = null;
    if (!gid) return;
    clearInterval(beat);
    beat = null;
    post(gid, false);
    gid = null;
  };
  return {
    typed(groupId, text) {
      clearTimeout(blurTimer);
      blurTimer = null;
      const key = groupId ? String(groupId) : null;
      if (!key || !String(text || "").trim()) return stop();
      if (gid === key) return undefined;
      stop();
      gid = key;
      post(key, true);
      beat = setInterval(() => post(key, true), TYPING_HEARTBEAT_MS);
      return undefined;
    },
    focus() {
      clearTimeout(blurTimer);
      blurTimer = null;
    },
    blur() {
      if (!gid) return;
      clearTimeout(blurTimer);
      blurTimer = setTimeout(stop, TYPING_BLUR_GRACE_MS);
    },
    stop,
  };
}

const isStopped = (v) => v === false || v === "false" || v === 0 || v === "0";

/**
 * A typing event meant for me, else null: { name, stopped } (the backend sends
 * the typist's full name, not their id).
 */
export function typingEventFor(data, myId) {
  if (!data || !Array.isArray(data.typingData) || myId == null) return null;
  const mine = data.typingData.find((t) => t && String(t.user) === String(myId));
  if (!mine) return null;
  if (!mine.isTyping || isStopped(mine.isTyping)) return { name: "", stopped: true };
  const name = String(data.fullName || mine.typingUserFullName || "").trim();
  return name ? { name, stopped: false } : null;
}

/**
 * Subscribes to typing events of one chat. onTyping({ name }) fires for every
 * event meant for me, onClear() on a stop or TYPING_CLEAR_MS after the last
 * one. Returns the unsubscribe function.
 */
export function subscribeTyping(socket, groupId, myId, onTyping, onClear) {
  if (!socket || typeof socket.on !== "function" || !groupId) return () => {};
  const eventName = String(groupId);
  let timer = null;
  const handler = (data) => {
    const who = typingEventFor(data, myId);
    if (!who) return;
    clearTimeout(timer);
    if (who.stopped) {
      onClear();
      return;
    }
    onTyping({ name: who.name });
    timer = setTimeout(() => onClear(), TYPING_CLEAR_MS);
  };
  socket.on(eventName, handler);
  return () => {
    clearTimeout(timer);
    try {
      socket.off(eventName, handler);
    } catch (_) {
      /* ignore */
    }
  };
}
